import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import {
  DataReconciliationBottomSection,
  DataReconciliationStep,
} from '@/modules/common/components/templates/data-reconciliation';
import { ProjectsReconciliationStep } from '@/modules/common/components/templates/data-reconciliation/components/steps/projects-reconciliation-step';
import { useReconciliationNavigation } from '@/modules/common/components/templates/data-reconciliation/hooks/use-reconciliation-navigation';
import { UnsavedChangesModal } from '@/modules/settings/components/unsaved-changes-modal';

export const ProjectsReconciliationStepPage: React.FC = () => {
  const { t } = useTranslation([
    'authenticated.dataReconciliation',
    'common',
    'authenticated.onboarding.common',
  ]);

  const { navigateToNextStep } = useReconciliationNavigation(DataReconciliationStep.Projects);

  const [isDirty, setIsDirty] = useState(false);

  const { isModalOpen, handleModalDiscard, handleModalCancel, allowNavigation } =
    useUnsavedChangesGuard({
      isDirty,
      onDiscard: () => setIsDirty(false),
    });

  const handleClickNext = useCallback(() => {
    allowNavigation();
    navigateToNextStep();
  }, [allowNavigation, navigateToNextStep]);

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden px-12 py-6">
      <Card className="flex w-full flex-1 flex-col overflow-hidden p-6">
        <h1 className="text-l font-semibold">{t('steps.projects')}</h1>
        <p className="mb-4 mt-2 text-sm text-muted-foreground">
          {t('stepPage.subtitle', { entity: t('entity.projects') })}
        </p>
        {/* Table with Scrollable Body */}
        <div className="flex min-h-0 flex-1 flex-col">
          <ProjectsReconciliationStep
            isInfiniteSizeTable={true}
            onClickNext={handleClickNext}
            bottomSection={DataReconciliationBottomSection(DataReconciliationStep.Projects)}
            onDirtyChange={setIsDirty}
          />
        </div>
      </Card>
      <UnsavedChangesModal
        open={isModalOpen}
        onClose={handleModalCancel}
        onDiscard={handleModalDiscard}
      />
    </div>
  );
};
